import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanMatchFn,
  RedirectCommand,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';

import { ViewportService } from '../services/viewport.service';

export const APP_COMING_SOON_PATH = '/app/em-breve';
export const APP_LANDING_PATH = '/cliente';

function routeChain(state: RouterStateSnapshot): ActivatedRouteSnapshot[] {
  const chain: ActivatedRouteSnapshot[] = [];
  let current: ActivatedRouteSnapshot | null = state.root;

  while (current) {
    chain.push(current);
    current = current.firstChild;
  }

  return chain.reverse();
}

function closestData<T>(chain: ActivatedRouteSnapshot[], key: string): T | undefined {
  const owner = chain.find((snapshot) => snapshot.routeConfig?.data?.[key] !== undefined);
  return owner?.routeConfig?.data?.[key] as T | undefined;
}

function moduleTitle(chain: ActivatedRouteSnapshot[]): string | undefined {
  const title = [...chain]
    .reverse()
    .map((snapshot) => snapshot.routeConfig?.title)
    .find((value) => typeof value === 'string');
  return title as string | undefined;
}

function redirectOutOfApp(moduleName: string | undefined, requestedUrl: UrlTree | string): RedirectCommand {
  const router = inject(Router);

  const queryParams =
    typeof requestedUrl === 'string' ? router.parseUrl(requestedUrl).queryParams : requestedUrl.queryParams;

  const cameFromLogin = !!queryParams['token'] || new URLSearchParams(inject(DOCUMENT).location.search).has('token');

  if (!router.navigated && cameFromLogin) {
    return new RedirectCommand(router.createUrlTree([APP_LANDING_PATH], { queryParams }), { replaceUrl: true });
  }

  const target = router.createUrlTree([APP_COMING_SOON_PATH], {
    queryParams: moduleName ? { module: moduleName } : {},
  });

  return new RedirectCommand(target, { browserUrl: requestedUrl });
}

function checkAppRoute(state: RouterStateSnapshot): true | RedirectCommand {
  if (!inject(ViewportService).isApp()) {
    return true;
  }

  const chain = routeChain(state);

  if (closestData<boolean>(chain, 'appEnabled') === true) {
    return true;
  }

  return redirectOutOfApp(closestData<string>(chain, 'moduleName') ?? moduleTitle(chain), state.url);
}

export const appRouteMatchGuard: CanMatchFn = (route) => {
  if (!inject(ViewportService).isApp() || route.data?.['appEnabled'] === true) {
    return true;
  }

  const router = inject(Router);
  const requestedUrl = router.getCurrentNavigation()?.extractedUrl ?? router.parseUrl(router.url);
  const moduleName = route.data?.['moduleName'] ?? (typeof route.title === 'string' ? route.title : undefined);

  return redirectOutOfApp(moduleName, requestedUrl);
};

export const appRouteChildGuard: CanActivateChildFn = (_route, state) => checkAppRoute(state);
